(() => {
  const DEFAULT_COLORS = ['#ffb199', '#7d4a57', '#6b7348', '#294635'];

  class OoConfetti extends HTMLElement {
    constructor() {
      super();
      this._started = false;
      this._canvas = null;
      this._raf = null;
    }

    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this.style.display = 'none';

      if (window.Shopify && Shopify.designMode) return;

      const start = this._dateFromParts(
        this.dataset.startYear,
        this.dataset.startMonth,
        this.dataset.startDay,
        false
      );
      const end = this._dateFromParts(
        this.dataset.endYear,
        this.dataset.endMonth,
        this.dataset.endDay,
        true
      );

      const now = new Date();
      if (start && now < start) return;
      if (end && now > end) return;

      const trigger = this.dataset.trigger || 'once_per_day';
      const storageKeyBase = this.dataset.storageKey || 'oo-confetti';

      try {
        if (trigger === 'once_per_visit') {
          const sessionKey = storageKeyBase;
          if (sessionStorage.getItem(sessionKey)) return;
          sessionStorage.setItem(sessionKey, '1');
        } else {
          const dayKey = now.toISOString().slice(0, 10);
          const storageKey = `${storageKeyBase}:${dayKey}`;
          if (localStorage.getItem(storageKey)) return;
          localStorage.setItem(storageKey, '1');
        }
      } catch (e) {
        return;
      }

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this._run();
    }

    _dateFromParts(yearStr, monthStr, dayStr, isEndOfDay) {
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);
      if (!year || !month || !day) return null;
      const date = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (Number.isNaN(date.getTime())) return null;
      if (isEndOfDay) date.setHours(23, 59, 59, 999);
      return date;
    }

    _run() {
      const durationMs = Number(this.dataset.durationMs || 4200);
      const pieceCountDesktop = Number(this.dataset.pieceCount || 220);
      const pieceCountMobile = Number(this.dataset.pieceCountMobile ?? pieceCountDesktop);
      const isMobile = window.innerWidth < 750;
      const pieceCount = isMobile ? pieceCountMobile : pieceCountDesktop;
      const colors = this._getColors();

      const canvas = document.createElement('canvas');
      canvas.style.position = 'fixed';
      canvas.style.left = '0';
      canvas.style.top = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '999999';
      canvas.setAttribute('aria-hidden', 'true');
      document.body.appendChild(canvas);
      this._canvas = canvas;

      const ctx = canvas.getContext('2d', { alpha: true });
      if (!ctx) return;

      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

      let w = 0;
      let h = 0;
      let originX = 0;
      let originY = 0;

      const setSize = () => {
        w = Math.max(1, window.innerWidth);
        h = Math.max(1, window.innerHeight);
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        originX = w * 0.5;
        originY = Math.max(80, h * 0.18);
      };

      setSize();
      window.addEventListener('resize', setSize);

      const rand = (min, max) => Math.random() * (max - min) + min;

      const pieces = [];
      const spreadDesktop = Number(this.dataset.spread ?? 1);
      const spreadMobile = Number(this.dataset.spreadMobile ?? spreadDesktop);
      const spread = isMobile ? spreadMobile : spreadDesktop;
      const spawnRadius = Math.min(180, w * 0.25) * spread;

      const makePiece = (i) => {
        const angle = rand(-Math.PI * 0.9, -Math.PI * 0.1);
        // Snelheid uitgedrukt in pixels/seconde zodat delta-tijd-correctie werkt.
        const speed = rand(200, 450);
        const x = originX + rand(-spawnRadius * 0.45, spawnRadius * 0.45);
        const y = originY + rand(-spawnRadius * 0.2, spawnRadius * 0.2);
        const size = rand(6, 12);
        const tilt = rand(-Math.PI, Math.PI);
        // Rotatiesnelheid in radialen/seconde.
        const spin = rand(-8, 8);
        // Zwaartekracht in pixels/seconde².
        const gravity = rand(180, 340);
        // Luchtweerstand als factor per seconde (dichter bij 1 = minder rem).
        const drag = rand(0.6, 0.75);
        const color = colors[i % colors.length];
        const shape = Math.random() < 0.35 ? 'circle' : 'rect';
        return {
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size,
          tilt,
          spin,
          gravity,
          drag,
          color,
          shape,
          life: 1,
          alpha: 1,
        };
      };

      for (let i = 0; i < pieceCount; i++) pieces.push(makePiece(i));

      const startAt = performance.now();
      let lastFrame = startAt;

      const tick = (now) => {
        const elapsed = now - startAt;
        const dt = Math.min((now - lastFrame) / 1000, 0.05); // seconden, max 50ms (voor tab-inactiviteit)
        lastFrame = now;
        const t = Math.min(1, elapsed / durationMs);

        ctx.clearRect(0, 0, w, h);

        const windStrength = Number(this.dataset.windStrength ?? 0.08);
        // Wind in pixels/seconde.
        const wind = (Math.sin(t * Math.PI * 2) * windStrength + (Math.random() - 0.5) * (windStrength * 0.25)) * 600;

        for (let i = 0; i < pieces.length; i++) {
          const p = pieces[i];
          // Pas drag toe als exponentieel verval per delta-tijd.
          const dragFactor = Math.pow(p.drag, dt);
          p.vx = (p.vx + wind * dt) * dragFactor;
          p.vy = p.vy * dragFactor + p.gravity * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.tilt += p.spin * dt;

          const fadeStart = 0.55;
          if (t > fadeStart) {
            const local = (t - fadeStart) / (1 - fadeStart);
            p.alpha = Math.max(0, 1 - local);
          }

          if (p.alpha <= 0) continue;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.tilt);
          ctx.globalAlpha = p.alpha;
          ctx.fillStyle = p.color;

          if (p.shape === 'circle') {
            ctx.beginPath();
            ctx.arc(0, 0, p.size * 0.22, 0, Math.PI * 2);
            ctx.fill();
          } else {
            const rw = p.size * rand(0.72, 1.1);
            const rh = p.size * rand(0.22, 0.32);
            ctx.fillRect(-rw * 0.5, -rh * 0.5, rw, rh);
          }
          ctx.restore();
        }

        if (t < 1) {
          this._raf = window.requestAnimationFrame(tick);
          return;
        }

        this._cleanup();
      };

      this._raf = window.requestAnimationFrame(tick);
    }

    _cleanup() {
      if (this._raf) window.cancelAnimationFrame(this._raf);
      this._raf = null;

      if (this._canvas && this._canvas.parentNode) this._canvas.parentNode.removeChild(this._canvas);
      this._canvas = null;
    }

    _getColors() {
      const raw = (this.dataset.colors || '').trim();
      if (!raw) return DEFAULT_COLORS;
      return raw
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
    }
  }

  if (!customElements.get('oo-confetti')) customElements.define('oo-confetti', OoConfetti);
})();

